<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
    <div class="py-6 px-4 sm:px-6 max-w-[1500px] mx-auto pb-10">

      <!-- Page header -->
      <div class="flex flex-row items-center justify-between gap-4 mb-5 flex-wrap">
        <div class="flex items-center gap-4 min-w-0">
          <button class="h-10 w-10 rounded-full bg-white border border-gray-200 hover:border-amber-300 hover:bg-amber-50 text-gray-500 hover:text-amber-700 flex items-center justify-center shadow-sm transition-colors flex-shrink-0" (click)="onCancel()" matTooltip="Back to shipments" aria-label="Back to shipments">
            <mat-icon class="!text-[20px]">arrow_back</mat-icon>
          </button>
          <div class="min-w-0">
            <div class="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
              <a routerLink="/shipments" [queryParams]="listQuery" class="hover:text-amber-700 transition-colors">Shipments</a>
              <mat-icon class="!text-[14px] !h-3.5 !w-3.5">chevron_right</mat-icon>
              <span class="text-gray-500">{{ isEditMode ? 'Edit' : 'New' }}</span>
            </div>
            <div class="flex flex-row items-center gap-2.5 flex-wrap">
              <h1 class="text-2xl font-bold text-gray-900 tracking-tight">{{ isEditMode ? reference : 'New shipment' }}</h1>
              @if (daysToEta; as d) {
                @if (d < 0) {
                  <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-0.5"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">schedule</mat-icon>{{ -d }} day{{ d === -1 ? '' : 's' }} past ETA</span>
                } @else if (d <= 7) {
                  <span class="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">Arrives in {{ d }} day{{ d === 1 ? '' : 's' }}</span>
                }
              }
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button class="h-10 w-10 rounded-xl bg-white border border-gray-200 text-gray-600 hover:border-amber-300 hover:bg-amber-50 flex items-center justify-center shadow-sm transition-colors" [matMenuTriggerFor]="moreMenu" aria-label="More actions" matTooltip="More">
            <mat-icon>more_horiz</mat-icon>
          </button>
          <mat-menu #moreMenu="matMenu" xPosition="before">
            @if (isCancelled) {
              <button mat-menu-item (click)="setStatus('BOOKED')"><mat-icon>restart_alt</mat-icon><span>Reopen as booked</span></button>
            } @else {
              <button mat-menu-item (click)="status = 'CANCELLED'"><mat-icon class="!text-red-500">block</mat-icon><span class="text-red-600">Mark as cancelled</span></button>
            }
            @if (isEditMode && canDelete) {
              <div class="my-1 border-t border-gray-100"></div>
              <button mat-menu-item (click)="confirmDelete()"><mat-icon class="!text-red-500">delete_forever</mat-icon><span class="text-red-600">Delete shipment</span></button>
            }
          </mat-menu>
        </div>
      </div>

      @if (loading) {
        <div class="po-card min-h-80 flex flex-col justify-center items-center">
          <mat-spinner diameter="32"></mat-spinner>
          <div class="text-sm text-gray-500 mt-3">Loading shipment&hellip;</div>
        </div>
      } @else {

        <!-- Stock state -->
        <div class="po-card !py-3 !px-4 mb-6 flex flex-row items-center gap-4 flex-wrap">
          @if (isCancelled) {
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <span class="h-8 w-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">block</mat-icon></span>
              <div class="min-w-0">
                <div class="text-sm font-semibold text-red-700">This shipment is cancelled</div>
                <div class="text-xs text-gray-500">Its lines won't count as stock on the way.</div>
              </div>
            </div>
            <button class="text-xs font-semibold px-3.5 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 transition-colors" (click)="setStatus('BOOKED')">Reopen as booked</button>
          } @else {
            <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider pl-1 hidden md:block">Stock state</div>
            <div class="po-steps flex-1 overflow-x-auto">
              @for (step of statusFlow; track step.value; let i = $index; let last = $last) {
                <button type="button" class="po-step" [class.done]="i < statusIndex" [class.current]="i === statusIndex"
                  (click)="setStatus(step.value)" [disabled]="step.value === 'RECEIVED'"
                  [matTooltip]="step.value === 'RECEIVED' ? 'Set when the yard validates the containers (Delivery validation › Receipts)' : i === statusIndex ? 'Current state' : 'Set state to ' + step.label">
                  <span class="po-step-circle">
                    @if (i < statusIndex) { <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">check</mat-icon> }
                    @else { <mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ step.icon }}</mat-icon> }
                  </span>
                  {{ step.label }}
                </button>
                @if (!last) { <span class="po-step-line" [class.done]="i < statusIndex"></span> }
              }
            </div>
            @if (containers.length > 1) {
              <span class="text-xs font-semibold rounded-full px-2.5 py-1 border whitespace-nowrap po-tabular" [ngClass]="clearedCount === containers.length ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-gray-50 text-gray-600 border-gray-200'"
                matTooltip="Each container clears on its own - set it on the container below">
                {{ clearedCount }} of {{ containers.length }} cleared
              </span>
            }
          }
        </div>

        <div class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start">

          <!-- ============ MAIN COLUMN ============ -->
          <div class="flex flex-col gap-6 min-w-0">

            <!-- Lines -->
            <div class="po-card">
              <div class="flex flex-row items-center justify-between gap-3 flex-wrap mb-5">
                <div class="flex items-center gap-3">
                  <span class="po-card-icon"><mat-icon class="!text-[20px] !h-5 !w-5">inventory_2</mat-icon></span>
                  <div>
                    <div class="font-bold text-gray-900 leading-tight">Shipment lines</div>
                    <div class="text-xs text-gray-400">
                      {{ lines.length }} line{{ lines.length === 1 ? '' : 's' }} &middot; {{ containers.length }} container{{ containers.length === 1 ? '' : 's' }}
                      @if (orderGroups.length) { &middot; {{ orderGroups.length }} purchase order{{ orderGroups.length === 1 ? '' : 's' }} }
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <button class="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50 transition-colors" [ngClass]="showPasteContainers ? '!border-amber-300 !bg-amber-50' : ''" (click)="showPasteContainers = !showPasteContainers" matTooltip="Paste a list of container numbers">
                      <mat-icon class="!text-[16px] !h-4 !w-4">content_paste</mat-icon> Paste containers
                    </button>
                    <button class="flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl bg-neutral-900 text-white hover:bg-neutral-700 transition-colors" (click)="addContainer()">
                      <mat-icon class="!text-[16px] !h-4 !w-4 !text-amber-400">add_box</mat-icon> Add container
                    </button>
                  </div>
                </div>

                @if (showPasteContainers) {
                  <div class="mb-5 rounded-2xl border border-amber-200 bg-amber-50/50 p-4">
                    <label class="po-label" for="sh-paste">Container numbers</label>
                    <textarea id="sh-paste" rows="2" class="po-field font-mono uppercase resize-y" placeholder="CLHU3293917, BHCU3103536, BHCU3163319, CAIU2036811" [(ngModel)]="pastedContainers" (keydown.meta.enter)="addPastedContainers()" (keydown.control.enter)="addPastedContainers()"></textarea>
                    <div class="mt-2 flex flex-wrap items-center gap-2">
                      @for (no of pastedContainerNos; track no) {
                        <span class="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-gray-700 bg-white border border-gray-200 rounded-md px-1.5 py-0.5"><mat-icon class="!text-[12px] !h-3 !w-3 !text-gray-400">view_in_ar</mat-icon>{{ no }}</span>
                        } @empty {
                        @if (!pastedAlreadyAdded.length) {
                          <span class="text-[11px] text-gray-500">Separate them with commas, spaces or new lines - empty containers get filled first.</span>
                        }
                      }
                      @for (no of pastedAlreadyAdded; track no) {
                        <span class="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-gray-400 bg-gray-100 border border-dashed border-gray-300 rounded-md px-1.5 py-0.5 line-through decoration-gray-300" matTooltip="Already on this shipment - not added again">{{ no }}</span>
                      }
                      @if (pastedAlreadyAdded.length) {
                        <span class="text-[11px] text-gray-500">{{ pastedAlreadyAdded.length }} already on this shipment</span>
                      }
                      <span class="flex-1"></span>
                      <button class="text-xs font-semibold px-3 py-1.5 rounded-lg text-gray-600 hover:bg-white transition-colors" (click)="showPasteContainers = false; pastedContainers = ''">Cancel</button>
                      <button class="text-xs font-semibold px-3.5 py-1.5 rounded-lg bg-neutral-900 text-white hover:bg-neutral-700 disabled:opacity-40 transition-colors" [disabled]="!pastedContainerNos.length" (click)="addPastedContainers()">
                        Add {{ pastedContainerNos.length || '' }} container{{ pastedContainerNos.length === 1 ? '' : 's' }}
                      </button>
                    </div>
                  </div>
                }

                <!-- Add from a purchase order -->
                <div class="flex flex-col sm:flex-row gap-2">
                  <div class="relative flex-1">
                    <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">playlist_add</mat-icon>
                    <input #poSearch #poTrigger="matAutocompleteTrigger" type="text" autocomplete="off" class="po-field !pl-11 !pr-10"
                      placeholder="Add lines from a purchase order - search by reference or supplier"
                      [value]="orderSearch" [matAutocomplete]="orderAuto" [disabled]="addingLines"
                      (input)="orderSearch = $any($event.target).value" />
                    @if (addingLines) {
                      <mat-spinner diameter="18" class="!absolute right-3 top-1/2 -translate-y-1/2"></mat-spinner>
                    }
                  </div>
                  @if (containers.length > 1) {
                    <div class="flex items-center gap-2 sm:w-64">
                      <span class="text-xs font-semibold text-gray-400 whitespace-nowrap">into</span>
                      <select class="po-field po-select font-semibold" [(ngModel)]="targetContainerKey" aria-label="Container to add the lines to">
                        @for (c of containers; track c.key) { <option [ngValue]="c.key">{{ containerLabel(c.key) }}</option> }
                      </select>
                    </div>
                  }
                </div>
                <mat-autocomplete #orderAuto="matAutocomplete" class="po-ac-panel" [displayWith]="orderDisplay"
                  [autoActiveFirstOption]="true" [hideSingleSelectionIndicator]="true" (optionSelected)="onOrderPicked($event.option.value)">
                  @for (o of filteredOrders; track o.id) {
                    <mat-option [value]="o">
                      <div class="flex items-center gap-3 py-2">
                        <span class="h-8 w-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[16px] !h-4 !w-4">receipt_long</mat-icon></span>
                        <div class="min-w-0 flex-1 leading-snug">
                          <div class="text-sm font-semibold text-gray-900">{{ o.reference }} <span class="font-normal text-gray-500">&middot; {{ o.supplierName || 'Unknown supplier' }}</span></div>
                          <div class="text-[11px] text-gray-400 truncate">
                            {{ o.status | titlecase }}@if (o.currency) { &middot; {{ o.currency }} }@if (o.store) { &middot; to {{ o.store }} }@if (o.expectedDeliveryDate) { &middot; due {{ o.expectedDeliveryDate | date: 'dd MMM' }} }
                          </div>
                        </div>
                        @if (linesForOrder(o.id); as n) {
                          <span class="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 whitespace-nowrap">{{ n }} on shipment</span>
                        }
                      </div>
                    </mat-option>
                    } @empty {
                    <mat-option disabled>
                      <div class="py-3 text-center text-sm text-gray-500 whitespace-normal">
                        {{ orderSearch ? 'No purchase order matches "' + orderSearch + '"' : 'No open purchase orders to ship' }}
                      </div>
                    </mat-option>
                  }
                </mat-autocomplete>
                <div class="text-[11px] text-gray-400 mt-1.5 pl-1">Picking a PO adds its outstanding lines, in its currency and incoterm.</div>

                @if (orderGroups.length) {
                  <div class="flex flex-wrap gap-2 mt-4">
                    @for (g of orderGroups; track g.id) {
                      <span class="inline-flex items-center gap-2 pl-3 pr-1 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs">
                        <span class="font-semibold text-gray-800">{{ g.reference }}</span>
                        @if (g.supplier) { <span class="text-gray-400 max-w-[160px] truncate">{{ g.supplier }}</span> }
                        <span class="text-gray-500 bg-white border border-gray-200 rounded-full px-1.5">{{ g.count }}</span>
                        <button class="h-5 w-5 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center" (click)="removeOrderLines(g.id)" [matTooltip]="'Remove ' + g.reference + '\'s lines'" [attr.aria-label]="'Remove ' + g.reference + ' lines'">
                          <mat-icon class="!text-[14px] !h-3.5 !w-3.5">close</mat-icon>
                        </button>
                      </span>
                    }
                  </div>
                }

                <!-- Pack by container capacity -->
                @if (lines.length) {
                  <div class="mt-5 flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-gradient-to-r from-gray-50 to-white px-4 py-3">
                    <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">view_week</mat-icon>
                    <span class="text-sm font-semibold text-gray-800">Pack into containers</span>
                    <span class="text-xs text-gray-400 mr-1 hidden md:inline">units per container</span>
                    @for (n of packSizes; track n) {
                      <button class="text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors po-tabular"
                        [ngClass]="lastPack?.size === n ? 'bg-neutral-900 text-amber-400 border-neutral-900' : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300 hover:bg-amber-50'"
                      (click)="packIntoContainers(n)" [matTooltip]="'Fill each container with ' + n + ' units, in order'">{{ n }}</button>
                    }
                    <div class="flex items-center">
                      <input type="number" min="1" step="1" placeholder="Custom" class="po-field po-sm po-num !w-24 !rounded-r-none" [(ngModel)]="customPackSize" (keydown.enter)="packIntoContainers(customPackSize)" aria-label="Custom units per container" />
                      <button class="text-xs font-bold px-3 py-[9px] rounded-r-[10px] border border-l-0 border-gray-200 bg-white text-gray-700 hover:bg-amber-50 hover:border-amber-300 disabled:opacity-40 transition-colors" [disabled]="!customPackSize" (click)="packIntoContainers(customPackSize)">Pack</button>
                    </div>
                    @if (lastPack) {
                      <span class="ml-auto inline-flex items-center gap-2 text-xs">
                        <span class="inline-flex items-center gap-1 font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 po-tabular"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">check_circle</mat-icon>{{ lastPack.summary }}</span>
                        <button class="font-semibold text-gray-500 hover:text-gray-900 underline-offset-2 hover:underline" (click)="undoPack()">Undo</button>
                      </span>
                    } @else {
                      <span class="ml-auto text-[11px] text-gray-400 hidden lg:inline">e.g. 116 units at 30 &rarr; 30 / 30 / 30 / 26</span>
                    }
                  </div>
                }

                @if (lines.length === 0 && containers.length <= 1) {
                  <div class="mt-5 rounded-2xl border-2 border-dashed border-gray-200 py-12 px-6 flex flex-col items-center text-center">
                    <span class="h-12 w-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon>directions_boat</mat-icon></span>
                    <div class="font-semibold text-gray-800 mt-3">No lines yet</div>
                    <div class="text-sm text-gray-400 mt-1 max-w-md">Search for a purchase order above to drop its outstanding lines in, or add a line by hand. Shipping in more than one container? Use <b class="text-gray-600">Add container</b> - any product can be split across them.</div>
                    <button class="mt-4 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50 transition-colors" (click)="addBlankLine()">
                      <mat-icon class="!text-[16px] !h-4 !w-4">add</mat-icon> Add a line by hand
                    </button>
                  </div>
                } @else {
                  <div class="mt-5 flex flex-col gap-5">
                    @for (c of containers; track c.key; let ci = $index) {
                      <div class="rounded-2xl border overflow-hidden transition-colors" [ngClass]="targetContainerKey === c.key && containers.length > 1 ? 'border-amber-300 shadow-[0_0_0_3px_#fef3c7]' : 'border-gray-200'">
                        <!-- Container header -->
                        <div class="flex flex-wrap items-center gap-3 px-4 py-3 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
                          <button type="button" class="flex items-center gap-2.5 min-w-0" (click)="targetContainerKey = c.key" [matTooltip]="containers.length > 1 ? 'Add new PO lines into this container' : ''">
                            <span class="h-9 w-9 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center flex-shrink-0"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">view_in_ar</mat-icon></span>
                            <span class="text-left leading-tight">
                              <span class="block text-sm font-bold text-gray-900">Container {{ ci + 1 }}</span>
                              <span class="block text-[11px] text-gray-400 po-tabular">{{ linesIn(c.key).length }} line{{ linesIn(c.key).length === 1 ? '' : 's' }} &middot; {{ unitsIn(linesIn(c.key)) | number: '1.0-2' }} units</span>
                            </span>
                          </button>
                          <div class="flex flex-wrap items-center gap-2 flex-1 justify-end">
                            @if (!isCancelled) {
                              @if (c.status === 'RECEIVED') {
                                <span class="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border" [ngClass]="containerStatusClass(c.status)" matTooltip="Checked in at the yard - stock booked">
                                  <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">lock</mat-icon>Received
                                </span>
                              } @else {
                                <button class="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors" [ngClass]="containerStatusClass(c.status)"
                                  [matMenuTriggerFor]="containerStateMenu" matTooltip="This container's state">
                                  <mat-icon class="!text-[15px] !h-[15px] !w-[15px]">{{ containerStep(c).icon }}</mat-icon>{{ containerStep(c).label }}
                                  <mat-icon class="!text-[16px] !h-4 !w-4 -mr-1 opacity-60">expand_more</mat-icon>
                                </button>
                                <mat-menu #containerStateMenu="matMenu">
                                  @for (step of statusFlow; track step.value) {
                                    <button mat-menu-item (click)="setContainerStatus(c, step.value)" [disabled]="step.value === 'RECEIVED'">
                                      <mat-icon>{{ step.value === c.status ? 'radio_button_checked' : step.icon }}</mat-icon><span>{{ step.label }}</span>
                                    </button>
                                  }
                                </mat-menu>
                              }
                              @if (c.clearedAt) {
                                <label class="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-700">
                                  Cleared
                                  <input type="date" class="po-field po-sm !w-[140px] !py-1 !text-xs" [(ngModel)]="c.clearedAt" aria-label="Date this container cleared customs" />
                                </label>
                              }
                            }
                            <div class="relative w-44">
                              <input type="text" placeholder="Container no." class="po-field po-sm font-mono uppercase" [ngClass]="containerNoLooksOff(c) ? '!border-amber-300' : ''" [(ngModel)]="c.containerNo" aria-label="Container number"
                                (blur)="splitContainerNo(c)" (keydown.enter)="splitContainerNo(c)" (paste)="pasteContainerNo(c)"
                                [matTooltip]="containerNoLooksOff(c) ? 'Usually 4 letters + 7 digits, e.g. MSKU1234567' : ''" />
                            </div>
                            @for (t of totalsFor(linesIn(c.key)); track t.currency) {
                              <span class="text-sm font-bold text-gray-900 whitespace-nowrap po-tabular pl-1">{{ currencySymbol(t.currency) }}{{ t.total | number: '1.2-2' }}</span>
                            }
                            @if (c.id) {
                              <a class="h-8 w-8 rounded-full text-gray-400 hover:bg-amber-50 hover:text-amber-700 flex items-center justify-center transition-colors" [routerLink]="['/shipments/container', c.id]" matTooltip="Open this container's page" aria-label="Open container page">
                                <mat-icon class="!text-[18px]">open_in_new</mat-icon>
                              </a>
                            }
                            @if (containers.length > 1) {
                              <button class="h-8 w-8 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors" (click)="removeContainer(c)"
                                [matTooltip]="linesIn(c.key).length ? 'Remove container (its lines move to ' + containerLabel(containers[0] === c ? containers[1].key : containers[0].key) + ')' : 'Remove container'" aria-label="Remove container">
                                <mat-icon class="!text-[18px]">delete_outline</mat-icon>
                              </button>
                            }
                          </div>
                        </div>

                        @if (linesIn(c.key).length) {
                          <ng-container *ngTemplateOutlet="lineTable; context: { $implicit: linesIn(c.key), key: c.key }"></ng-container>
                        } @else {
                          <div class="px-4 py-6 text-center text-sm text-gray-400">
                            Empty -
                            @if (containers.length > 1) { <button class="font-semibold text-amber-700 hover:underline" (click)="pickPoInto(c.key)">pick a PO into it</button>, split a line here, or }
                            <button class="font-semibold text-amber-700 hover:underline" (click)="addBlankLine(c.key)">add a line by hand</button>
                          </div>
                        }
                        @if (linesIn(c.key).length) {
                          <div class="px-4 pb-3">
                            <button class="po-add-line !py-2 !text-xs" (click)="addBlankLine(c.key)">
                              <mat-icon class="!text-[16px] !h-4 !w-4">add</mat-icon> Add a line by hand to {{ containerLabel(c.key) }}
                            </button>
                          </div>
                        }
                      </div>
                    }

                    @if (unassignedLines.length) {
                      <div class="rounded-2xl border border-amber-300 overflow-hidden">
                        <div class="flex items-center gap-2.5 px-4 py-3 bg-amber-50 border-b border-amber-100">
                          <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-amber-600">warning_amber</mat-icon>
                          <span class="text-sm font-semibold text-amber-900">Not in a container</span>
                          <span class="text-xs text-amber-800">Use the ⋯ on each line to move it into one.</span>
                        </div>
                        <ng-container *ngTemplateOutlet="lineTable; context: { $implicit: unassignedLines, key: null }"></ng-container>
                      </div>
                    }
                  </div>
                }

                <!-- Lines of one container -->
                <ng-template #lineTable let-group let-key="key">
                  <div class="overflow-x-auto">
                    <table class="po-table w-full text-sm">
                      <thead>
                        <tr class="text-left border-b border-gray-100">
                          <th class="py-2.5 pl-4 pr-1 w-8">#</th>
                          <th class="py-2.5 px-2 w-36">PO &middot; Incoterm</th>
                          <th class="py-2.5 px-3 min-w-[260px]">Product</th>
                          <th class="py-2.5 px-2 min-w-[170px]">Quantity</th>
                          <th class="py-2.5 px-2 min-w-[180px]">Unit price</th>
                          <th class="py-2.5 px-3 w-28 text-right">Amount</th>
                          <th class="py-2.5 pr-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (line of group; track line; let i = $index) {
                          <tr class="po-row border-b border-gray-50 last:border-b-0 align-top transition-colors hover:bg-amber-50/30">
                            <td class="py-3 pl-4 pr-1 pt-[18px] text-xs font-semibold text-gray-300 po-tabular">{{ i + 1 }}</td>
                            <td class="py-3 px-2">
                              <div class="flex flex-col gap-1.5">
                                <select class="po-field po-sm po-select" [ngClass]="!line.purchaseOrderId && line.productName ? '!border-amber-300 !bg-amber-50' : ''" [(ngModel)]="line.purchaseOrderId" (ngModelChange)="onLineOrderChange(line)" aria-label="Purchase order">
                                  <option [ngValue]="null">Choose PO</option>
                                  @for (o of orderChoices; track o) {
                                    <option [ngValue]="o.id">{{ o.reference }}</option>
                                  }
                                </select>
                                <select class="po-field po-sm po-select !py-1 !text-xs !bg-gray-50" [(ngModel)]="line.incoterm" aria-label="Incoterm">
                                  <option [ngValue]="null">No incoterm</option>
                                  @for (opt of incotermOptions; track opt) {
                                    <option [value]="opt">{{ opt }}</option>
                                  }
                                </select>
                              </div>
                            </td>
                            <td class="py-3 px-3">
                              @if (line.purchaseOrderId && poLines[line.purchaseOrderId]) {
                                <!-- Product from the line's PO: picking fills qty, unit, price, currency and incoterm -->
                                <div class="relative">
                                  <mat-icon class="absolute left-2 top-1/2 -translate-y-1/2 !text-gray-400 !text-[16px] !h-4 !w-4 pointer-events-none">receipt_long</mat-icon>
                                  <input type="text" autocomplete="off" class="po-field po-sm !pl-7 !pr-7" [ngClass]="line.productId ? '!border-emerald-300' : ''"
                                    [placeholder]="'Pick a product from ' + orderReference(line.purchaseOrderId)"
                                    [value]="line.productName" [title]="line.productName" [matAutocomplete]="poLineAuto"
                                    (input)="onPoLineTyped(line, $any($event.target).value)" />
                                  @if (line.productId) {
                                    <mat-icon class="absolute right-2 top-1/2 -translate-y-1/2 !text-[16px] !h-4 !w-4 !text-emerald-500 pointer-events-none" matTooltip="From the purchase order">check_circle</mat-icon>
                                  }
                                </div>
                                <mat-autocomplete #poLineAuto="matAutocomplete" class="po-ac-panel" panelWidth="min(560px, 92vw)" [displayWith]="poLineDisplay"
                                  [autoActiveFirstOption]="true" [hideSingleSelectionIndicator]="true" (optionSelected)="pickPoLine(line, $event.option.value)">
                                  <div class="px-3 pt-1.5 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">On {{ orderReference(line.purchaseOrderId) }}</div>
                                  @for (p of poLineOptions(line); track $index) {
                                    <mat-option [value]="p">
                                      <div class="flex items-start gap-3 py-2">
                                        <div class="min-w-0 flex-1 leading-snug">
                                          <div class="text-sm font-medium text-gray-900 whitespace-normal">{{ p.productName }}</div>
                                          <div class="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
                                            @if (p.sku) { <span class="font-mono bg-gray-100 rounded px-1.5">{{ p.sku }}</span> }
                                            <span>{{ currencySymbol(p.currency) }}{{ p.unitPrice | number: '1.2-2' }} / {{ p.unitOfMeasure }}</span>
                                            @if (p.incoterm) { <span>&middot; {{ p.incoterm }}</span> }
                                          </div>
                                        </div>
                                        <div class="shrink-0 text-right leading-tight">
                                          <div class="text-xs font-semibold po-tabular" [ngClass]="remainingFor(p, line) > 0 ? 'text-emerald-700' : 'text-gray-400'">{{ remainingFor(p, line) | number: '1.0-2' }} to ship</div>
                                          <div class="text-[10px] text-gray-400 po-tabular">of {{ p.quantity | number: '1.0-2' }} outstanding</div>
                                        </div>
                                      </div>
                                    </mat-option>
                                    } @empty {
                                    <mat-option disabled>
                                      <div class="py-3 text-center text-sm text-gray-500 whitespace-normal">Nothing on this PO matches - it'll be kept as typed</div>
                                    </mat-option>
                                  }
                                </mat-autocomplete>
                              } @else {
                                <app-product-picker
                                  size="sm"
                                  [placeholder]="line.purchaseOrderId ? 'Loading the PO\'s products…' : 'Choose a PO first, or search products'"
                                  panelWidth="min(560px, 92vw)"
                                  noMatchHint="That's fine - it'll be kept as a custom description"
                                  [products]="products"
                                  [selectedId]="line.productId"
                                  [(query)]="line.productName"
                                  (queryEdited)="onLineProductEdited(line)"
                                  (picked)="onLineProductPicked(line, $event)"
                                  />
                              }
                              @if (line.sku) {
                                <a class="mt-1 pl-1 inline-flex items-center gap-1 max-w-[320px] text-[11px] text-gray-400 font-mono hover:text-amber-700 hover:underline underline-offset-2 transition-colors"
                                  [routerLink]="['/inventory']" [queryParams]="{ q: line.sku }" target="_blank" rel="noopener" matTooltip="Open in inventory (new tab)">
                                  <span class="truncate">{{ line.sku }}</span><mat-icon class="!text-[12px] !h-3 !w-3 flex-shrink-0">open_in_new</mat-icon>
                                </a>
                              }
                            </td>
                            <td class="py-3 px-2">
                              <div class="sh-join">
                                <input type="number" min="0" step="0.01" class="po-field po-sm po-num min-w-0 flex-1" [ngClass]="(line.productName && !(line.quantity > 0)) || overShipped(line) ? '!border-red-300' : ''" [(ngModel)]="line.quantity" aria-label="Quantity" />
                                <select class="po-field po-sm po-select !w-[88px] flex-shrink-0 !bg-gray-50 text-xs font-semibold" [(ngModel)]="line.unitOfMeasure" aria-label="Unit">
                                  @for (opt of unitOptions; track opt) {
                                    <option [value]="opt">{{ opt }}</option>
                                  }
                                </select>
                              </div>
                              @if (overShipped(line); as o) {
                                <div class="text-[10px] font-semibold text-red-600 mt-1 po-tabular" matTooltip="Across every line of this shipment">{{ o.total | number: '1.0-2' }} shipped of {{ o.outstanding | number: '1.0-2' }} outstanding</div>
                              }
                            </td>
                            <td class="py-3 px-2">
                              <div class="sh-join">
                                <select class="po-field po-sm po-select !w-[80px] flex-shrink-0 !bg-gray-50 text-xs font-semibold" [(ngModel)]="line.currency" aria-label="Currency">
                                  @for (opt of currencyOptions; track opt) {
                                    <option [value]="opt">{{ opt }}</option>
                                  }
                                </select>
                                <input type="number" min="0" step="0.0001" class="po-field po-sm po-num min-w-0 flex-1" [(ngModel)]="line.unitPrice" aria-label="Unit price" />
                              </div>
                            </td>
                            <td class="py-3 px-3 pt-[15px] text-right font-semibold text-gray-900 whitespace-nowrap po-tabular">{{ currencySymbol(line.currency) }}{{ lineSubtotal(line) | number: '1.2-2' }}</td>
                            <td class="py-3 pr-3 pt-[11px] text-center">
                              <button class="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-800 flex items-center justify-center transition-all" [matMenuTriggerFor]="lineMenu" aria-label="Line actions" matTooltip="Split, move or remove">
                                <mat-icon class="!text-[20px]">more_horiz</mat-icon>
                              </button>
                              <mat-menu #lineMenu="matMenu" xPosition="before">
                                @if (containers.length > 1) {
                                  <button mat-menu-item (click)="spreadLine(line)"><mat-icon>view_week</mat-icon><span>Spread across all {{ containers.length }} containers</span></button>
                                }
                                <button mat-menu-item [matMenuTriggerFor]="splitMenu"><mat-icon>call_split</mat-icon><span>Split into</span></button>
                                @if (containers.length > 1 || !key) {
                                  <button mat-menu-item [matMenuTriggerFor]="moveMenu"><mat-icon>move_down</mat-icon><span>Move to</span></button>
                                }
                                <button mat-menu-item (click)="removeLine(line)"><mat-icon class="!text-red-500">delete_outline</mat-icon><span class="text-red-600">Remove line</span></button>
                              </mat-menu>
                              <mat-menu #splitMenu="matMenu">
                                <div class="px-4 pt-2 pb-1 text-[11px] text-gray-400 max-w-[220px]">Half the quantity goes to:</div>
                                @for (t of containers; track t.key) {
                                  <button mat-menu-item (click)="splitLine(line, t.key)"><mat-icon>view_in_ar</mat-icon><span>{{ containerLabel(t.key) }}{{ t.key === key ? ' (same)' : '' }}</span></button>
                                }
                              </mat-menu>
                              <mat-menu #moveMenu="matMenu">
                                @for (t of containers; track t.key) {
                                  @if (t.key !== key) {
                                    <button mat-menu-item (click)="moveLine(line, t.key)"><mat-icon>view_in_ar</mat-icon><span>{{ containerLabel(t.key) }}</span></button>
                                  }
                                }
                              </mat-menu>
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </ng-template>

                <!-- Notes + totals -->
                <div class="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
                  <div>
                    <label class="po-label" for="sh-notes">Notes</label>
                    <textarea id="sh-notes" rows="4" placeholder="Customs, demurrage, delays - anything worth remembering" class="po-field resize-y" [(ngModel)]="notes"></textarea>
                  </div>
                  <div class="rounded-2xl bg-gray-50 border border-gray-100 px-5 py-4 text-sm po-tabular">
                    <div class="flex flex-row justify-between text-gray-500"><span>Lines</span><span class="text-gray-800">{{ lines.length }}</span></div>
                    <div class="flex flex-row justify-between text-gray-500 mt-1.5"><span>Units</span><span class="text-gray-800">{{ totalUnits | number: '1.0-2' }}</span></div>
                    <div class="mt-3 pt-3 border-t border-gray-200 flex flex-col gap-1.5">
                      @for (t of totalsByCurrency; track t.currency) {
                        <div class="flex flex-row justify-between items-baseline">
                          <span class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total {{ t.currency }}</span>
                          <span class="text-xl font-bold text-gray-900">{{ currencySymbol(t.currency) }}{{ t.total | number: '1.2-2' }}</span>
                        </div>
                        } @empty {
                        <div class="flex flex-row justify-between items-baseline">
                          <span class="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total</span>
                          <span class="text-xl font-bold text-gray-300">&mdash;</span>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <!-- Voyage -->
              <div class="po-card">
                <div class="flex items-center justify-between gap-3 mb-5 flex-wrap">
                  <div class="flex items-center gap-3">
                    <span class="po-card-icon"><mat-icon class="!text-[20px] !h-5 !w-5">directions_boat</mat-icon></span>
                    <div>
                      <div class="font-bold text-gray-900 leading-tight">Voyage</div>
                      <div class="text-xs text-gray-400">Where it sails from, where it lands, and when</div>
                    </div>
                  </div>
                  @if (sailingDays; as days) {
                    <span class="text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-3 py-1">{{ days }} day{{ days === 1 ? '' : 's' }} at sea</span>
                  }
                </div>

                <div class="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(140px,1.4fr)_minmax(0,1fr)] gap-5 items-start">
                  <!-- From -->
                  <div class="flex flex-col gap-3">
                    <div>
                      <label class="po-label" for="sh-pol">From &middot; port of loading</label>
                      <select id="sh-pol" class="po-field po-select font-semibold" [(ngModel)]="portOfLoading">
                        <option value="">Choose port</option>
                        @for (opt of portChoices(loadingPorts, portOfLoading); track opt) {
                          <option [value]="opt">{{ opt }}</option>
                        }
                      </select>
                    </div>
                    <div>
                      <label class="po-label" for="sh-etd">ETD &middot; departs</label>
                      <input id="sh-etd" type="date" class="po-field" [(ngModel)]="etd" />
                    </div>
                  </div>

                  <!-- Track -->
                  <div class="md:pt-7 flex flex-col justify-center">
                    <div class="sh-track mx-2">
                      <div class="sh-track-line"></div>
                      <div class="sh-track-fill" [class.done]="voyageDone" [style.width.%]="journeyPct ?? 0"></div>
                      <span class="sh-track-end" style="left: 0" [class.reached]="!!journeyPct" [class.done]="voyageDone"></span>
                      <span class="sh-track-end" style="left: 100%" [class.done]="voyageDone"></span>
                      <div class="sh-ship" [class.done]="voyageDone" [style.left.%]="journeyPct ?? 0">
                        <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">{{ voyageDone ? 'anchor' : 'directions_boat' }}</mat-icon>
                      </div>
                    </div>
                    <div class="text-center text-xs mt-2 font-medium" [ngClass]="dateError || (daysToEta ?? 0) < 0 ? 'text-red-600' : 'text-gray-500'">{{ voyageCaption }}</div>
                  </div>

                  <!-- To -->
                  <div class="flex flex-col gap-3">
                    <div>
                      <label class="po-label" for="sh-pod">To &middot; port of discharge</label>
                      <select id="sh-pod" class="po-field po-select font-semibold" [(ngModel)]="portOfDischarge">
                        <option value="">Choose port</option>
                        @for (opt of portChoices(dischargePorts, portOfDischarge); track opt) {
                          <option [value]="opt">{{ opt }}</option>
                        }
                      </select>
                    </div>
                    <div>
                      <label class="po-label" for="sh-eta">ETA &middot; arrives</label>
                      <input id="sh-eta" type="date" class="po-field" [ngClass]="dateError ? '!border-red-300' : ''" [(ngModel)]="eta" />
                    </div>
                  </div>
                </div>
              </div>

            </div>
            <!-- ============ /MAIN COLUMN ============ -->

            <!-- ============ SIDEBAR ============ -->
            <div class="flex flex-col gap-6">

              <!-- Summary -->
              <div class="rounded-[18px] bg-neutral-900 text-white p-6 shadow-sm po-tabular">
                <div class="flex items-center justify-between">
                  <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">Shipment value</span>
                  <span class="text-[11px] font-semibold rounded-full bg-white/10 px-2 py-0.5" [ngClass]="isCancelled ? 'text-red-300' : 'text-amber-300'">{{ isCancelled ? 'Cancelled' : statusLabel }}</span>
                </div>
                @for (t of totalsByCurrency; track t.currency; let first = $first) {
                  <div [class]="first ? 'text-3xl font-bold mt-1.5 tracking-tight' : 'text-lg font-semibold text-neutral-300'">{{ currencySymbol(t.currency) }}{{ t.total | number: '1.2-2' }} <span class="text-xs font-semibold text-neutral-500">{{ t.currency }}</span></div>
                  } @empty {
                  <div class="text-3xl font-bold mt-1.5 tracking-tight text-neutral-600">&mdash;</div>
                }
                <div class="mt-1 text-xs text-neutral-400">{{ containers.length }} container{{ containers.length === 1 ? '' : 's' }} &middot; {{ lines.length }} line{{ lines.length === 1 ? '' : 's' }} &middot; {{ orderGroups.length }} PO{{ orderGroups.length === 1 ? '' : 's' }} &middot; to {{ store }}</div>

                <div class="mt-5 pt-5 border-t border-white/10 grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-sm">
                  <div class="min-w-0">
                    <div class="text-[10px] uppercase tracking-wider text-neutral-500">From</div>
                    <div class="font-semibold truncate" [class.text-neutral-600]="!portOfLoading">{{ portOfLoading || 'Not set' }}</div>
                    <div class="text-[11px] text-neutral-400">{{ etd ? (etd | date: 'dd MMM yyyy') : '-' }}</div>
                  </div>
                  <mat-icon class="!text-neutral-600">arrow_forward</mat-icon>
                  <div class="min-w-0 text-right">
                    <div class="text-[10px] uppercase tracking-wider text-neutral-500">To</div>
                    <div class="font-semibold truncate" [class.text-neutral-600]="!portOfDischarge">{{ portOfDischarge || 'Not set' }}</div>
                    <div class="text-[11px] text-neutral-400">{{ eta ? (eta | date: 'dd MMM yyyy') : '-' }}</div>
                  </div>
                </div>
              </div>

              <!-- Destination -->
              <div class="po-card">
                <div class="font-bold text-gray-900 mb-1">Destination yard</div>
                <div class="text-xs text-gray-400 mb-4">Where the stock is booked in once it's delivered</div>
                <div class="po-seg">
                  @for (opt of storeOptions; track opt) {
                    <button type="button" [class.active]="store === opt" (click)="store = opt">
                      <mat-icon class="!text-[16px] !h-4 !w-4">warehouse</mat-icon>{{ opt }}
                    </button>
                  }
                </div>
              </div>

              <!-- Carrier -->
              <div class="po-card">
                <div class="flex items-center gap-2 mb-4">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">sailing</mat-icon>
                  <div class="font-bold text-gray-900">Carrier</div>
                </div>
                <div class="flex flex-col gap-4">
                  <div>
                    <label class="po-label" for="sh-line">Shipping line</label>
                    <input id="sh-line" type="text" placeholder="e.g. Maersk" class="po-field" [(ngModel)]="shippingLine" />
                  </div>
                  <div>
                    <label class="po-label" for="sh-vessel">Vessel / voyage</label>
                    <input id="sh-vessel" type="text" placeholder="e.g. Maersk Kendal / 412W" class="po-field" [(ngModel)]="vesselDetails" />
                  </div>
                </div>
              </div>

              <!-- Documents -->
              <div class="po-card">
                <div class="flex items-center gap-2 mb-4">
                  <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">description</mat-icon>
                  <div class="font-bold text-gray-900">Documents</div>
                </div>
                <div class="grid grid-cols-2 gap-x-3 gap-y-4">
                  <div class="col-span-2">
                    <label class="po-label" for="sh-bl">Bill of lading</label>
                    <input id="sh-bl" type="text" placeholder="BL number" class="po-field font-mono uppercase" [(ngModel)]="billOfLading" />
                  </div>
                  <div>
                    <label class="po-label" for="sh-inv">Invoice no.</label>
                    <input id="sh-inv" type="text" placeholder="Supplier invoice" class="po-field" [(ngModel)]="invoiceNumber" />
                  </div>
                  <div>
                    <label class="po-label" for="sh-inv-date">Invoice date</label>
                    <input id="sh-inv-date" type="date" class="po-field" [(ngModel)]="invoiceDate" />
                  </div>
                </div>
              </div>

            </div>
            <!-- ============ /SIDEBAR ============ -->

          </div>
        }

      </div>

      <!-- Sticky save bar -->
      @if (!loading) {
        <div class="sticky bottom-0 z-20 bg-white/90 backdrop-blur border-t border-gray-200 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.08)]">
          <div class="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div class="hidden sm:flex items-center gap-3 text-sm min-w-0">
              <span class="text-gray-400 truncate">{{ isEditMode ? 'Editing ' + reference : 'New shipment' }}</span>
              @for (b of saveBlockers; track b) {
                <span class="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 whitespace-nowrap"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">radio_button_unchecked</mat-icon>{{ b }}</span>
                } @empty {
                <span class="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 whitespace-nowrap"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">check_circle</mat-icon>Ready to save</span>
              }
            </div>
            <div class="flex items-center gap-3 ml-auto">
              @for (t of totalsByCurrency; track t.currency) {
                <div class="text-right leading-tight mr-1 po-tabular">
                  <div class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{{ t.currency }}</div>
                  <div class="text-base font-bold text-gray-900 whitespace-nowrap">{{ currencySymbol(t.currency) }}{{ t.total | number: '1.2-2' }}</div>
                </div>
              }
              <button class="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors" (click)="onCancel()">Cancel</button>
              <button
                class="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:from-amber-300 hover:to-amber-400 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                [disabled]="saving" (click)="onSave()">
                @if (saving) { <mat-spinner diameter="16"></mat-spinner> } @else { <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">check</mat-icon> }
                {{ saving ? 'Saving…' : (isEditMode ? 'Save changes' : 'Create shipment') }}
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  </app-main-layout>
